import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChartOrderItem, DataType } from '../../utils/types/analytics/analytics_types';
import { MediumColors } from './Colors';
import { DashletIconGroup } from './DashletIconGroup';

type ColumnChartAssessmentDashletProps = {
  id: string;
  data: Array<DataType>;
  title: string;
  dashletItem: ChartOrderItem;
  onDragStart: (item: ChartOrderItem) => void;
  onDragEnter: (item: ChartOrderItem) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  fullScreenDetected: boolean;
  setFullScreenDetected: (fullscreen: boolean) => void;
  handleDashletVisibility: (item: ChartOrderItem) => void;
  editMode: boolean;
};

export const ColumnChartAssessmentDashlet = ({
  id,
  data,
  title,
  dashletItem,
  onDragStart,
  onDragEnter,
  onDragOver,
  onDrop,
  fullScreenDetected,
  setFullScreenDetected,
  handleDashletVisibility,
  editMode,
}: ColumnChartAssessmentDashletProps) => {
  const handleDragStart = () => {
    if (onDragStart) {
      onDragStart(dashletItem);
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragEnter) {
      onDragEnter(dashletItem);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragOver) {
      onDragOver(event);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDrop) {
      onDrop(event);
    }
  };

  return (
    <Paper
      variant="outlined"
      className="group"
      sx={{
        position: 'relative',
        width: '100%',
        gridColumn: { xs: 'span 1', lg: 'span 2' },
        borderRadius: '16px',
        height: '100%',
        p: 2.5,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
          borderColor: 'primary.main',
        },
      }}
    >
      <DashletIconGroup
        id={id}
        dashletItem={dashletItem}
        setFullScreenDetected={setFullScreenDetected}
        handleDashletVisibility={handleDashletVisibility}
        editMode={editMode}
      />

      <Box
        id={id}
        draggable
        onDragStart={handleDragStart}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          width: '100%',
          height: '100%',
          opacity: dashletItem.hidden ? 0.4 : 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: fullScreenDetected ? '1.5rem' : '0.875rem',
            color: 'text.secondary',
            mb: 2,
            textAlign: 'center',
          }}
        >
          {title}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          {!data || data.length === 0 ? (
            <Box
              sx={{
                height: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                fontSize: '0.875rem',
              }}
            >
              No data to preview
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={fullScreenDetected ? 500 : 300}>
              <BarChart
                layout="vertical"
                data={data}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" label={{ value: '', position: 'insideBottom' }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 14 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="max" fill={MediumColors.RED} name="Time Allocated" />
                <Bar dataKey="value" fill={MediumColors.GREEN} name="Time Completed" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
